import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/base/Seo';
import ComicCard from '@/components/base/ComicCard';
import { CATEGORIES, COMICS } from '@/mocks/comics';

const SORT_OPTIONS = [
  { key: 'popular', label: '综合' },
  { key: 'latest', label: '最新' },
  { key: 'rating', label: '高分' },
  { key: 'views', label: '热度' },
];

export default function CategoryPage() {
  const { slug = 'reXue' } = useParams();
  const navigate = useNavigate();
  const [sort, setSort] = useState('popular');

  const category = CATEGORIES.find((c) => c.slug === slug) ?? CATEGORIES[0];

  const list = useMemo(() => {
    let result = COMICS.filter((c) => c.categorySlug === slug);
    if (sort === 'latest') result = [...result].sort((a, b) => (a.updated < b.updated ? 1 : -1));
    if (sort === 'rating') result = [...result].sort((a, b) => b.rating - a.rating);
    if (sort === 'views') result = [...result].sort((a, b) => b.views - a.views);
    return result;
  }, [slug, sort]);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.manhuadao.cn/' },
        { '@type': 'ListItem', position: 2, name: `${category.name}漫画` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${category.name}漫画推荐`,
      description: `${category.name}漫画精选推荐：${category.desc}，来漫画岛发现值得入坑的${category.name}作品。`,
      url: `https://www.manhuadao.cn/category/${category.slug}`,
    },
  ];

  const switchCategory = (s: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/category/${s}`);
    setSort('popular');
  };

  return (
    <>
      <Seo
        title={`${category.name}漫画推荐 · 漫画岛 | 发现好漫`}
        description={`${category.name}漫画精选推荐，漫画岛编辑精选每日推荐热门${category.name}新作与潜力佳作，探索推荐挖掘好货不踩雷。`}
        canonicalPath={`/category/${category.slug}`}
        jsonLd={jsonLd}
      />
      <Navbar />

      <section className="relative bg-gradient-to-b from-primary-600 to-primary-700 pt-24 pb-12 md:pt-28 md:pb-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="mb-4 flex items-center gap-1.5 text-sm text-background-100/80">
            <Link to="/" className="flex items-center gap-1 hover:text-background-50">
              <i className="ri-home-4-line"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{category.name}漫画</span>
          </nav>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-3xl text-background-50 md:text-5xl">{category.name}漫画</h1>
              <p className="mt-2 text-base text-background-100/90">{category.desc}</p>
              <p className="mt-1 text-sm text-background-200/80">共收录 {category.count} 部作品 · 编辑精选持续更新</p>
            </div>
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-background-50/15 text-background-50 backdrop-blur">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-8 md:py-10">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="分类切换">
              {CATEGORIES.map((c) => {
                const active = c.slug === category.slug;
                return (
                  <button
                    key={c.slug}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => switchCategory(c.slug)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap transition-colors ${
                      active ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${c.icon} text-base`}></i>
                    {c.name}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1" role="tablist" aria-label="排序">
              {SORT_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  role="tab"
                  aria-selected={sort === o.key}
                  onClick={() => setSort(o.key)}
                  className={`rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap transition-colors ${
                    sort === o.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {list.length === 0 ? (
            <div className="mt-12 flex flex-col items-center justify-center py-16 text-center">
              <i className="ri-inbox-line text-5xl text-background-300"></i>
              <p className="mt-4 text-base text-foreground-600">该分类下暂无作品，去其他分类看看吧。</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
              {list.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}